// constants/api.js
// API Configuration and Route Constants - Synchronized with Mobile App
// Base URL - VOG Ecommerce Backend
// Use REACT_APP_BACKEND_URL (without /api/v1) or REACT_APP_API_URL (with /api/v1).
// If you see ERR_CERT_COMMON_NAME_INVALID, set REACT_APP_BACKEND_URL to a host with a valid SSL cert (e.g. your production API).
const BACKEND_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace(/\/api\/v1\/?$/, '')
    : null) ||
  'https://api.st-cael.org';

/** Origin without `/api/v1` — REST uses API_BASE_URL; Socket.IO uses this. */
export const BACKEND_BASE_URL = BACKEND_BASE;
export const API_BASE_URL = `${BACKEND_BASE}/api/v1`;

// API Endpoints (synchronized with mobile app)
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: "/auth/login",
    SIGNUP: "/auth/signup",
    SIGNUP_SELLER: "/auth/signup-seller",
    FORGOT_PASSWORD: "/auth/forgot-password",
    RESET_PASSWORD: "/auth/reset-password",
    VERIFY_EMAIL: "/auth/verify-email",
    RESEND_VERIFICATION: "/auth/resend-verification",
    CHECK_EMAIL: "/auth/check-email",
    REFRESH_TOKEN: "/auth/refresh-token",
    SOCIAL_LOGIN: "/auth/social-login",
    FIREBASE_SOCIAL_LOGIN: "/auth/firebase-social-login",
    LOGOUT: "/auth/logout",
  },

  // User Profile
  USER: {
    PROFILE: "/user/profile",
    UPDATE_PROFILE: "/user/profile",
    CHANGE_PASSWORD: "/user/change-password",
    DELETE_ACCOUNT: "/user/delete-account",
    UPLOAD_AVATAR: "/user/avatar",
    REMOVE_PROFILE_PICTURE: "/user/profile-picture",
    PUSH_TOKEN: "/user/push-token",
    LAST_VIEWS: "/user/last-views",
  },

  // Products
  PRODUCTS: {
    GET_ALL: "/products",
    GET_BY_ID: "/products/:id",
    SEARCH: "/products",
    SUGGESTIONS: "/products/suggestions",
    FLASH_SALES: "/products?isFlash=1",
    RECOMMENDED: "/products?isRecommended=1",
    FEATURED: "/products?isRecommended=1",
    BY_CATEGORY: "/products/category/:categoryId",
    REVIEWS: "/products/:id/reviews",
    BID: "/products/:id/bid",
    OFFER: "/products/:id/offers",
    INQUIRY: "/products/:id/inquiry",
    BY_SELLER: "/products/seller/:sellerId",
  },

  // Categories
  CATEGORIES: {
    GET_ALL: "/categories",
    CREATE: "/categories",
    GET_SUBCATEGORIES: "/categories/:id/subcategories",
    GET_BY_ID: "/categories/:id",
  },

  // Cart
  CARTS: {
    GET: "/carts",
    ADD_ITEM: "/carts",
    ADD_BID: "/bids/:bidId/add-to-cart",
    ADD_OFFER: "/offers/:offerId/add-to-cart",
    INCREASE: "/carts/increase/:itemId",
    DECREASE: "/carts/decrease/:itemId",
    UPDATE: "/carts/:itemId",
    REMOVE_ITEM: "/carts/:itemId",
    CLEAR: "/carts",
  },

  // Orders
  ORDERS: {
    CREATE: "/orders",
    GET_ALL: "/orders",
    GET_BY_ID: "/orders/:id",
    CANCEL: "/orders/:id/cancel",
    TRACK: "/orders/:id/track",
  },

  // Wishlist
  WISHLIST: {
    GET: "/user/wishlist",
    ADD: "/user/wishlist",
    REMOVE: "/user/wishlist/:productId",
    CLEAR: "/user/wishlist/clear",
  },

  // Payment
  PAYMENTS: {
    PROCESS: "/payments/process",
  },
  PAYMENT_OPTIONS: {
    GET_ALL: "/payment-options",
  },

  // Reviews
  REVIEWS: {
    GET_ALL: "/reviews",
    CREATE: "/reviews",
    GET_BY_PRODUCT: "/reviews/product/:productId",
  },

  // Offers & conversations (canonical — Marketplace model)
  OFFERS: {
    MY: "/offers/my",
    ADD_TO_CART: "/offers/:offerId/add-to-cart",
    ACCEPT: "/offers/:offerId/accept",
    REJECT: "/offers/:offerId/reject",
    COUNTER: "/offers/:offerId/counter",
  },
  CONVERSATIONS: {
    LIST: "/conversations",
    BY_SELLER: "/conversations/seller/:sellerId",
    BY_PRODUCT: "/conversations/product/:productId",
    DETAIL: "/conversations/:conversationId",
    ATTACH_PRODUCT: "/conversations/:conversationId/products/:productId",
    MESSAGES: "/conversations/:conversationId/messages",
    READ: "/conversations/:conversationId/read",
    CLOSE: "/conversations/:conversationId/close",
  },

  // Bidding (deprecated aliases — still supported by backend)
  BIDS: {
    MY_BIDS: "/bids/my-bids",
    MESSAGES: "/bids/messages",
    CONVERSATIONS: "/bids/conversations",
    DELETE: "/bids/:bidId",
  },

  // Admin endpoints
  ADMIN: {
    OFFERS: {
      ACCEPT: "/admin/offers/:offerId/accept",
      REJECT: "/admin/offers/:offerId/reject",
      CANCEL: "/admin/offers/:offerId/cancel",
    },
    BIDS: {
      ACCEPT: "/admin/bids/:bidId/accept",
      REJECT: "/admin/bids/:bidId/reject",
      CANCEL: "/admin/bids/:bidId/cancel",
      COUNTER_OFFER: "/admin/bids/:bidId/counter-offer",
    },
  },

  // Address
  ADDRESS: {
    GET_ALL: "/user/address",
    ADD: "/user/address",
    UPDATE: "/user/address/:id",
    DELETE: "/user/address/:id",
    SET_DEFAULT: "/user/address/:id/default",
  },

  // Notifications
  NOTIFICATIONS: {
    GET_ALL: "/notifications?target=buyer",
    UNREAD_COUNT: "/notifications/unread-count?target=buyer",
    STREAM: "/notifications/stream",
    MARK_READ: "/notifications/:id/read",
    MARK_ALL_READ: "/notifications/read-all?target=buyer",
    DELETE: "/notifications/:id",
    CLEAR_ALL: "/notifications?target=buyer",
  },

  // Support
  SUPPORT: {
    CONTACT: "/support/contact",
    FAQ: "/support/faq",
    REPORT_ISSUE: "/support/report",
  },

  // Shipping Zones
  SHIPPING_ZONES: {
    GET_ALL: "/shipping-zones",
    GET_ACTIVE: "/shipping-zones/active",
    CALCULATE: "/shipping-zones/calculate/:neighborhoodCode",
  },

  // Sellers
  SELLERS: {
    GET_BY_ID: "/sellers/:sellerId",
  },

  // Marketing campaigns
  MARKETING: {
    ACTIVE: "/marketing-campaigns/active",
    BY_PLACEMENT: "/marketing-campaigns/placement/:placement",
    VALIDATE_COUPON: "/marketing-campaigns/validate-coupon",
  },

  // Boutiques (shop discovery & ranking)
  BOUTIQUES: {
    LIST: "/boutiques",
    TOP_PERFORMANCE: "/boutiques/top/performance",
    PRODUCTS: "/boutiques/:boutiqueId/products",
  },
};

// HTTP Methods
export const HTTP_METHODS = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  PATCH: "PATCH",
  DELETE: "DELETE",
};

// Request Headers
export const DEFAULT_HEADERS = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

// Response Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500,
};

// Error Messages
export const API_ERROR_MESSAGES = {
  NETWORK_ERROR: "Network error. Please check your connection.",
  TIMEOUT_ERROR: "Request timeout. Please try again.",
  UNAUTHORIZED: "Session expired. Please login again.",
  FORBIDDEN: "Access denied.",
  NOT_FOUND: "Resource not found.",
  SERVER_ERROR: "Server error. Please try again later.",
  VALIDATION_ERROR: "Please check your input and try again.",
};

// Request Timeout (in milliseconds)
export const REQUEST_TIMEOUT = 30000; // 30 seconds

// Helper function to build URLs with path parameters
export const buildUrl = (endpoint, params = {}) => {
  let url = endpoint;
  Object.keys(params).forEach((key) => {
    url = url.replace(`:${key}`, params[key]);
  });
  return url;
};

// Helper function to build query string
// Automatically includes lang parameter for backend language support
export const buildQueryString = (params = {}) => {
  // Get current language using utility functions
  let currentLang = 'fr'; // Default
  try {
    // Import utilities dynamically to avoid circular dependencies
    // Priority: localStorage > cookie > browser > default
    currentLang = localStorage.getItem('i18nextLng') || currentLang;
    
    // If not in localStorage, try cookie
    if (!currentLang || currentLang === 'fr') {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'i18nextLng' || name === 'lang') {
          currentLang = decodeURIComponent(value);
          break;
        }
      }
    }
    
    // Extract 2-letter code (e.g., 'fr-FR' -> 'fr')
    if (currentLang && currentLang.length > 2) {
      currentLang = currentLang.substring(0, 2).toLowerCase();
    }
    
    // Validate supported languages
    if (!currentLang || !['fr', 'en'].includes(currentLang)) {
      currentLang = 'fr';
    }
  } catch (e) {
    // Fallback to default if any error
    currentLang = 'fr';
  }
  
  // Add lang parameter if not already present
  const paramsWithLang = { ...params };
  if (!paramsWithLang.lang && !paramsWithLang.language) {
    paramsWithLang.lang = currentLang;
  }
  
  // Filter out undefined, null, and empty string values
  const filteredParams = Object.entries(paramsWithLang).filter(
    ([, value]) => value !== undefined && value !== null && value !== ""
  );
  
  const query = new URLSearchParams(Object.fromEntries(filteredParams)).toString();
  return query ? `?${query}` : "";
};

